import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import api from '../../services/api';

interface ImportFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ValidationResult {
  valid: boolean;
  formData?: {
    name: string;
    description: string;
    is_template: boolean;
  };
  stats?: {
    sections: number;
    categories: number;
    questions: number;
    categoryWeightings: Record<string, number>;
  };
  sections?: Array<{
    name: string;
    orden: number;
    questionCount: number;
  }>;
  categories?: Array<{
    name: string;
    orden: number;
    color?: string;
  }>;
  errors?: string[];
}

const ImportFormDialog: React.FC<ImportFormDialogProps> = ({ open, onClose, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const steps = ['Seleccionar archivo', 'Validar datos', 'Confirmar importación'];

  const handleClose = () => {
    setActiveStep(0);
    setSelectedFile(null);
    setValidation(null);
    setError(null);
    onClose();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/forms/template', {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_form.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError('Error al descargar el template');
      console.error(err);
    }
  };

  const handleValidate = async () => {
    if (!selectedFile) {
      setError('Por favor selecciona un archivo');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post('/forms/import/validate', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setValidation(response.data);

      if (response.data.valid) {
        setActiveStep(2);
      } else {
        setError(response.data.errors?.join(', ') || 'Error de validación');
      }
    } catch (err: any) {
      setError(err.response?.data?.errors?.[0] || err.response?.data?.error || 'Error al validar archivo');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setImporting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post('/forms/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        onSuccess();
        handleClose();
      } else {
        setError(response.data.error || 'Error al importar formulario');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Error al importar formulario';
      setError(errorMsg);
      console.error('[ImportForm] Error completo:', err);
      console.error('[ImportForm] Response:', err.response?.data);
    } finally {
      setImporting(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Instrucciones:</strong>
              </Typography>
              <Typography variant="body2" component="div">
                1. Descarga el template de Excel<br/>
                2. Completa las hojas "Formulario" y "Preguntas"<br/>
                3. Sube el archivo para validarlo<br/>
                4. Confirma la importación
              </Typography>
            </Alert>

            <Box display="flex" gap={2} mb={3}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadTemplate}
              >
                Descargar Template
              </Button>
            </Box>

            <Box
              component="label"
              sx={{
                display: 'block',
                p: 4,
                border: '2px dashed',
                borderColor: 'primary.main',
                bgcolor: 'background.default',
                textAlign: 'center',
                borderRadius: 1,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                overflow: 'visible',
                '&:hover': {
                  borderColor: '#FFEB5D',
                  bgcolor: 'rgba(255, 235, 93, 0.05)'
                }
              }}
            >
              <input
                type="file"
                accept=".xlsx,.xls"
                hidden
                onChange={handleFileSelect}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <UploadIcon 
                  sx={{ 
                    fontSize: 48, 
                    color: '#FFEB5D', 
                    mb: 2
                  }} 
                />
                <Typography variant="h6" gutterBottom>
                  Selecciona un archivo Excel
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Formatos aceptados: .xlsx, .xls (máx. 5MB)
                </Typography>
              </Box>
            </Box>

            {selectedFile && (
              <Box mt={2}>
                <Alert severity="success" icon={<CheckIcon />}>
                  <Typography variant="body2">
                    <strong>Archivo seleccionado:</strong> {selectedFile.name} 
                    ({(selectedFile.size / 1024).toFixed(2)} KB)
                  </Typography>
                </Alert>
              </Box>
            )}
          </Box>
        );

      case 2:
        if (!validation || !validation.valid) {
          return (
            <Alert severity="error">
              Error en la validación
            </Alert>
          );
        }

        return (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }} icon={<CheckIcon />}>
              El archivo es válido y está listo para importar
            </Alert>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                📋 Información del Formulario
              </Typography>
              <Box>
                <Typography><strong>Nombre:</strong> {validation.formData?.name}</Typography>
                <Typography><strong>Descripción:</strong> {validation.formData?.description}</Typography>
                <Box component="div" sx={{ mt: 1 }}>
                  <Typography component="span"><strong>Es Template:</strong>{' '}</Typography>
                  <Chip 
                    label={validation.formData?.is_template ? 'SÍ' : 'NO'} 
                    size="small"
                    color={validation.formData?.is_template ? 'primary' : 'default'}
                  />
                </Box>
              </Box>
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                📊 Estadísticas
              </Typography>
              <Box display="flex" gap={3} flexWrap="wrap">
                <Box>
                  <Typography variant="h4" color="primary">{validation.stats?.sections}</Typography>
                  <Typography variant="body2" color="textSecondary">Secciones</Typography>
                </Box>
                <Box>
                  <Typography variant="h4" color="primary">{validation.stats?.categories}</Typography>
                  <Typography variant="body2" color="textSecondary">Categorías</Typography>
                </Box>
                <Box>
                  <Typography variant="h4" color="primary">{validation.stats?.questions}</Typography>
                  <Typography variant="body2" color="textSecondary">Preguntas</Typography>
                </Box>
              </Box>

              {validation.stats?.categoryWeightings && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    ⚖️ Ponderación por Categoría:
                  </Typography>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    {Object.entries(validation.stats.categoryWeightings).map(([category, weight]: [string, any]) => {
                      const isValid = Math.abs(weight - 100) <= 0.1;
                      return (
                        <Box key={category} sx={{ minWidth: 120 }}>
                          <Typography 
                            variant="h6" 
                            color={isValid ? 'success.main' : 'warning.main'}
                          >
                            {weight.toFixed(1)}%
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {category}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {validation.stats?.categoryWeightings && 
               Object.values(validation.stats.categoryWeightings).some((w: any) => Math.abs(w - 100) > 0.1) && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Algunas categorías no tienen exactamente 100% de ponderación. Esto puede afectar los cálculos de cumplimiento.
                </Alert>
              )}
            </Paper>

            {validation.sections && validation.sections.length > 0 && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  📑 Secciones
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Orden</TableCell>
                        <TableCell>Nombre</TableCell>
                        <TableCell align="right">Preguntas</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validation.sections.map((section) => (
                        <TableRow key={section.name}>
                          <TableCell>{section.orden}</TableCell>
                          <TableCell>{section.name}</TableCell>
                          <TableCell align="right">{section.questionCount}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {validation.categories && validation.categories.length > 0 && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  🏷️ Categorías
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {validation.categories.map((category) => (
                    <Chip 
                      key={category.name}
                      label={category.name}
                      sx={{ bgcolor: category.color, color: 'white' }}
                    />
                  ))}
                </Box>
              </Paper>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Importar Formulario desde Excel
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {renderStepContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading || importing}>
          Cancelar
        </Button>

        {activeStep === 0 && (
          <Button
            variant="contained"
            onClick={handleValidate}
            disabled={!selectedFile || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <InfoIcon />}
          >
            {loading ? 'Validando...' : 'Validar Archivo'}
          </Button>
        )}

        {activeStep === 2 && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={importing}
            startIcon={importing ? <CircularProgress size={20} /> : <CheckIcon />}
            color="primary"
          >
            {importing ? 'Importando...' : 'Confirmar Importación'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportFormDialog;
